/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React from 'react'
import { VictoryPie, VictoryContainer, VictoryTooltip } from 'victory'
import colors from '@sportninja/common/constants/appColors'
import Text from '../../ts-components/Text'
import type { AudienceBreakdownData } from './AudienceBreakdownSection'
import type { AudienceReach, SelectedChannels } from '../utils/audienceReach'
import { inAppFootnote } from '../utils/audienceReach'
import { audienceLegendRows } from '../utils/audienceGroups'

const CHART_SIZE = 200

const EMPTY_DATA = [{ x: ' ', y: 1 }]
const EMPTY_COLORS = [colors.NEUTRAL_600]

interface AudienceDonutChartProps {
  data: AudienceBreakdownData | null
  hasCalculated: boolean
  /** Channel-scoped numbers, derived once in utils/audienceReach. */
  reach: AudienceReach
  channels: SelectedChannels
  onViewRecipients?: () => void
}

const AudienceDonutChart: React.FC<AudienceDonutChartProps> = ({
  data,
  hasCalculated,
  reach,
  channels,
  onViewRecipients,
}) => {
  /*
   * Rows, slices and colours are ONE list, shared with the pre-send confirmation — which
   * chose its own rows from the legacy counts until they disagreed on screen. See
   * utils/audienceGroups for why membership picks the rows and reach values them.
   */
  const legendRows = audienceLegendRows(reach)

  /*
   * Summed over every row, not just the ones with a slice. Summing the legacy-filtered
   * set meant an audience with no joined owner or admin blanked the whole ring while its
   * reachable people sat in rows underneath it.
   */
  const reachTotal = legendRows.reduce((sum, row) => sum + row.value, 0)
  const showEmpty = !hasCalculated || !data || reachTotal === 0

  const slices = legendRows.filter((row) => row.value > 0)

  const chartData = showEmpty
    ? EMPTY_DATA
    : slices.map((row) => ({ x: row.label, y: row.value }))

  const chartColors = showEmpty ? EMPTY_COLORS : slices.map((row) => row.color)

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        opacity: ${hasCalculated && data ? 1 : 0.45};
        transition: opacity 0.4s ease;
      `}
    >
      {/* Chart with center label */}
      <div
        css={css`
          position: relative;
          width: ${CHART_SIZE}px;
          height: ${CHART_SIZE}px;
        `}
      >
        <VictoryPie
          data={chartData}
          colorScale={chartColors}
          width={CHART_SIZE}
          height={CHART_SIZE}
          innerRadius={70}
          padding={10}
          labels={
            showEmpty
              ? () => ''
              : ({ datum }) => `${datum.x}: ${datum.y.toLocaleString()}`
          }
          labelComponent={
            <VictoryTooltip
              flyoutStyle={{
                fill: colors.SECONDARY_300,
                stroke: colors.NEUTRAL_600,
                strokeWidth: 1,
              }}
              style={{
                fill: colors.WHITE,
                fontSize: 14,
              }}
              cornerRadius={4}
              flyoutPadding={{ top: 6, bottom: 6, left: 10, right: 10 }}
            />
          }
          containerComponent={
            <VictoryContainer
              responsive={false}
              width={CHART_SIZE}
              height={CHART_SIZE}
            />
          }
          animate={{ duration: 600, easing: 'cubicInOut' }}
        />
        {/* Center label overlay */}
        <div
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            pointer-events: none;
          `}
        >
          <Text variant='heading6' weight='bold' color={colors.WHITE}>
            {hasCalculated && data ? reach.total.toLocaleString() : '--'}
          </Text>
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_100}
          >
            Recipients
          </Text>
          {/*
           * The denominator, stated as a ratio rather than as a second free-standing
           * number. Two stacked figures gave the reader no grammar for how they relate —
           * and on an audience with no opt-outs they are the same figure twice, which
           * reads as a rendering fault. "of N" makes the second one a denominator.
           *
           * Never "users": a third of this audience is on a roster with no account, and
           * the switch below is captioned "Include Non-SportNinja Users".
           */}
          {hasCalculated && data && (
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              of {reach.inAudience.toLocaleString()} in audience
            </Text>
          )}
        </div>
      </div>

      {/* Legend */}
      {hasCalculated && data && legendRows.length > 0 && (
        <div
          css={css`
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 4px 24px;
            width: 100%;
          `}
        >
          {legendRows.map((row) => (
            <div
              key={row.key}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <span
                css={css`
                  width: 10px;
                  height: 10px;
                  border-radius: 50%;
                  background-color: ${row.color};
                  flex-shrink: 0;
                `}
              />
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
              >
                {row.label}: {row.value.toLocaleString()}
              </Text>
            </div>
          ))}
        </div>
      )}

      {/* Reach footnotes — the honest read of the number above. */}
      {hasCalculated && data && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            width: 100%;
          `}
        >
          {reach.unjoined > 0 && (
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {reach.unjoined.toLocaleString()} have not joined
            </Text>
          )}

          {inAppFootnote(reach, channels) && (
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_40}
            >
              {inAppFootnote(reach, channels)}
            </Text>
          )}

          {reach.noEmail > 0 && (
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_40}
            >
              {reach.noEmail.toLocaleString()} cannot be reached by email
            </Text>
          )}

          {/*
            A real button rather than a text link. Everyone who calculates an audience
            wants to look at it, and a small tinted line under three grey footnotes is the
            easiest thing on the panel to miss.
          */}
          {onViewRecipients && (
            <button
              type='button'
              onClick={onViewRecipients}
              css={css`
                margin-top: 8px;
                padding: 8px 20px;
                border: 1px solid ${colors.PRIMARY};
                border-radius: 6px;
                background: transparent;
                cursor: pointer;
                transition: background-color 0.15s ease;
                &:hover {
                  background: rgba(242, 180, 0, 0.12);
                }
              `}
            >
              <Text
                variant='paragraphSmall'
                weight='semiBold'
                color={colors.PRIMARY}
              >
                View Audience
              </Text>
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default AudienceDonutChart
